'use client';

import dynamic from 'next/dynamic';
import { useAppClientDataSelector } from '@/core';
import { SuspenseLoader } from '@knapsack/toby';
import { NextJsPageProps } from '@/types';
import { redirect } from 'next/navigation';
import { useKsNav } from '@/utils/routes';

const BadRoute = dynamic(() => import('@/core/pages/bad-route'));
const CustomPage = dynamic(() => import('@/core/site/pages/custom-page'));

const Page = ({ params: { pageId } }: NextJsPageProps) => {
  const { createPageUrl } = useKsNav();
  const pageExists = useAppClientDataSelector(
    ({ customPagesState }) => !!customPagesState.pages[pageId]?.id,
    {
      dependencies: [pageId],
    },
  );

  const pageTabIds = useAppClientDataSelector(
    ({ customPagesState }) => customPagesState.pages[pageId]?.tabIds || [],
    {
      dependencies: [pageId],
    },
  );

  /**
   * If the page exists and has tabs, redirect to the first tab
   */
  if (pageExists && pageTabIds.length > 0) {
    return redirect(createPageUrl({ pageId, tabId: pageTabIds[0] }));
  }

  /**
   * If the page exists and has no tabs, render the page
   */
  if (pageExists && pageTabIds.length === 0) {
    return (
      <SuspenseLoader>
        <CustomPage pageId={pageId} tabId={undefined} />
      </SuspenseLoader>
    );
  }

  /**
   * If the page doesn't exist, render the bad route
   */
  return (
    <SuspenseLoader>
      <BadRoute title={`Page not found: ${pageId}`} section="document" />
    </SuspenseLoader>
  );
};

export default Page;
